/*
* Under-Brim Message add-on.
*
* Adds an optional, paid customization ("Under-Brim Message", +$15 AUD) to eligible
* hat products. The $15 charge is a real, separate line item (a hidden "add-on"
* product) that is kept in lock-step with the hat's line item via a shared,
* randomly generated `_addon_id` line item property. Both the customer's message
* and the linking id are submitted as ordinary line item properties on the hat's
* own <product-form>, so the existing Add to cart flow (Dawn's product-form.js)
* is left completely untouched — this script only adds a second, linked line
* item after a successful add, and keeps quantities/removals in sync afterwards.
*/
(() => {
if (window.UnderBrimMessage) return;

// pubsub.js / constants.js load earlier in <head> (see layout/theme.liquid)
// and this file is loaded with `defer`, so `subscribe` and `PUB_SUB_EVENTS`
// are already available as plain globals by the time this IIFE runs. Fall
// back to no-ops rather than throwing if that ever changes.
window.UnderBrimMessage =
typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined'
? { subscribe, PUB_SUB_EVENTS }
: { subscribe: () => () => {}, PUB_SUB_EVENTS: { cartUpdate: 'cart-update' } };

const ADDON_HANDLE = 'under-brim-message-addon';
const MAX_CHARS = 30;

function generateId() {
if (window.crypto && window.crypto.randomUUID) return window.crypto.randomUUID();
return `ubm_${Date.now()}_${Math.random().toString(36).slice(2)}`;
}

function fetchConfigJson(body) {
return {
method: 'POST',
headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
body: JSON.stringify(body),
};
}

/* -------------------------------------------------------------------- *
* Cart section refresh helpers                                         *
* -------------------------------------------------------------------- */

function collectSectionIds() {
// localKey -> actual Shopify section id to request / read from the response
const ids = {};
if (document.querySelector('cart-drawer')) {
ids.cartDrawer = 'cart-drawer';
ids.cartIconBubble = 'cart-icon-bubble';
}
const mainItems = document.getElementById('main-cart-items');
if (mainItems && mainItems.dataset.id) {
ids.mainCartItems = mainItems.dataset.id;
}
const mainFooter = document.getElementById('main-cart-footer');
if (mainFooter && mainFooter.dataset.id) {
ids.mainCartFooter = mainFooter.dataset.id;
}
if (!ids.cartIconBubble && document.getElementById('cart-icon-bubble')) {
ids.cartIconBubble = 'cart-icon-bubble';
}
return ids;
}

function parseSection(html) {
return new DOMParser().parseFromString(html, 'text/html');
}

function applyCartResponse(sectionIds, sections) {
if (!sections) return;

// Cart drawer: use its own renderContents() so open/close + animation state
// (which live only in the DOM, not in the server response) are preserved.
const drawer = document.querySelector('cart-drawer');
if (drawer && sectionIds.cartDrawer && sections[sectionIds.cartDrawer] && typeof drawer.renderContents === 'function') {
drawer.renderContents({ sections });
} else if (sectionIds.cartIconBubble && sections[sectionIds.cartIconBubble]) {
const bubbleHtml = sections[sectionIds.cartIconBubble];
const bubble = document.getElementById('cart-icon-bubble');
if (bubble) {
const doc = parseSection(bubbleHtml);
const fresh = doc.querySelector('.shopify-section') || doc.body.firstElementChild;
if (fresh) bubble.innerHTML = fresh.innerHTML;
}
}

// Full cart page: no transient UI state to preserve, so a wholesale swap
// of the custom element (and footer) is simplest and most robust.
if (sectionIds.mainCartItems && sections[sectionIds.mainCartItems]) {
const doc = parseSection(sections[sectionIds.mainCartItems]);
const freshItems = doc.querySelector('cart-items');
const oldItems = document.querySelector('cart-items');
if (freshItems && oldItems) oldItems.replaceWith(freshItems);
}
if (sectionIds.mainCartFooter && sections[sectionIds.mainCartFooter]) {
const doc = parseSection(sections[sectionIds.mainCartFooter]);
const freshFooter = doc.getElementById('main-cart-footer');
const oldFooter = document.getElementById('main-cart-footer');
if (freshFooter && oldFooter) oldFooter.replaceWith(freshFooter);
}
}

async function refreshCartUI() {
const sectionIds = collectSectionIds();
const values = Object.values(sectionIds);
if (!values.length) return;
try {
const response = await fetch(`${window.location.pathname}?sections=${values.join(',')}`);
if (!response.ok) return;
const sections = await response.json();
applyCartResponse(sectionIds, sections);
} catch (error) {
console.error('[under-brim-message] failed to refresh cart UI', error);
}
}

/* -------------------------------------------------------------------- *
* Cart line reconciliation (add / quantity sync / removal)             *
* -------------------------------------------------------------------- */

let reconciling = false;
let reconcileAgain = false;

async function reconcileAddonLines() {
if (reconciling) {
reconcileAgain = true;
return;
}
reconciling = true;
try {
const cart = await fetch('/cart.js', { headers: { Accept: 'application/json' } }).then((r) => r.json());

const hatLines = cart.items.filter(
(item) => item.handle !== ADDON_HANDLE && item.properties && item.properties._addon_id
);
const addonLines = cart.items.filter((item) => item.handle === ADDON_HANDLE);

let changed = false;

for (const hatLine of hatLines) {
const addonId = hatLine.properties._addon_id;
const match = addonLines.find((line) => line.properties && line.properties._addon_id === addonId);
if (!match) {
const addonVariantId = await getAddonVariantId();
if (!addonVariantId) continue;
await fetch('/cart/add.js', fetchConfigJson({
id: addonVariantId,
quantity: hatLine.quantity,
properties: { _addon_id: addonId },
}));
changed = true;
} else if (match.quantity !== hatLine.quantity) {
await fetch('/cart/change.js', fetchConfigJson({ id: match.key, quantity: hatLine.quantity }));
changed = true;
}
}

for (const addonLine of addonLines) {
const addonId = addonLine.properties && addonLine.properties._addon_id;
const hasHat = hatLines.some((line) => line.properties._addon_id === addonId);
if (!hasHat) {
await fetch('/cart/change.js', fetchConfigJson({ id: addonLine.key, quantity: 0 }));
changed = true;
}
}

if (changed) await refreshCartUI();
} catch (error) {
console.error('[under-brim-message] reconciliation failed', error);
} finally {
reconciling = false;
if (reconcileAgain) {
reconcileAgain = false;
reconcileAddonLines();
}
}
}

let reconcileTimer;
function scheduleReconcile(delay = 250) {
    clearTimeout(reconcileTimer);
    reconcileTimer = setTimeout(reconcileAddonLines, delay);
    }

    let addonVariantIdPromise = null;
    function getAddonVariantId() {
    const el = document.querySelector('under-brim-message[data-addon-variant-id]');
    if (el && el.dataset.addonVariantId) return Promise.resolve(el.dataset.addonVariantId);
    if (!addonVariantIdPromise) {
    addonVariantIdPromise = fetch('/products/under-brim-message-addon.js', { headers: { Accept: 'application/json' } })
    .then((response) => (response.ok ? response.json() : null))
    .then((product) => (product && product.variants && product.variants[0] ? product.variants[0].id : null))
    .catch(() => null);
    }
    return addonVariantIdPromise;
    }

    /* -------------------------------------------------------------------- *
    * <under-brim-message> — the product page control                      *
    * -------------------------------------------------------------------- */

    class UnderBrimMessage extends HTMLElement {
    constructor() {
    super();
    this.checkbox = this.querySelector('[data-under-brim-toggle]');
    this.textarea = this.querySelector('[data-under-brim-textarea]');
    this.addonIdInput = this.querySelector('[data-under-brim-addon-id]');
    this.counter = this.querySelector('[data-under-brim-count]');
    this.form = this.closest('form') || document.getElementById(this.dataset.formId);
    this.submitButton = this.form ? this.form.querySelector('[type="submit"]') : null;

    this.pendingSubmission = null;

    if (this.checkbox) {
    this.checkbox.disabled = false;
    this.checkbox.addEventListener('change', this.onToggle.bind(this));
    }
    if (this.textarea) {
    this.textarea.addEventListener('input', this.onInput.bind(this));
    }
    if (this.form) {
    this.form.addEventListener('submit', this.onFormSubmit.bind(this));
    }

    this.updateCounter();
    this.updateSubmitState();
    }

    connectedCallback() {
    this.cartUpdateUnsubscriber = window.UnderBrimMessage.subscribe(
    window.UnderBrimMessage.PUB_SUB_EVENTS.cartUpdate,
    (event) => {
    if (event.source !== 'product-form') return;
    this.handleCartUpdate(event);
    }
    );
    }

    disconnectedCallback() {

  if (this.cartUpdateUnsubscriber) this.cartUpdateUnsubscriber();
}

onToggle() {
// Showing/hiding .recipient-fields is handled entirely by CSS (the same
// ":checked ~ .recipient-fields" sibling-selector reveal the theme's own
// gift-card recipient form uses) — this only needs to keep the fields'
// disabled state (and therefore form-submission inclusion) in sync.
const checked = this.checkbox.checked;
if (this.textarea) this.textarea.disabled = !checked;
if (this.addonIdInput) this.addonIdInput.disabled = !checked;

if (checked) {
if (this.addonIdInput) this.addonIdInput.value = generateId();
if (this.textarea) this.textarea.focus();
} else {
if (this.textarea) this.textarea.value = '';
if (this.addonIdInput) this.addonIdInput.value = '';
this.updateCounter();
}
this.updateSubmitState();
}

onInput() {
if (this.textarea.value.length > MAX_CHARS) {
this.textarea.value = this.textarea.value.slice(0, MAX_CHARS);
}
this.updateCounter();
this.updateSubmitState();
}

updateCounter() {
if (this.counter) this.counter.textContent = String(this.textarea ? this.textarea.value.length : 0);
}

isValid() {
if (!this.checkbox || !this.checkbox.checked) return true;
const message = this.textarea ? this.textarea.value.trim() : '';
return message.length > 0 && message.length <= MAX_CHARS;
}

updateSubmitState() {
if (!this.submitButton) return;
const shouldBlock = this.checkbox && this.checkbox.checked && !this.isValid();
if (shouldBlock) {
// Only ever set/clear this ourselves, and remember that we're the
// ones holding it disabled — never clobber a disabled state that
// Dawn itself set for an unrelated reason (e.g. a sold-out variant).
this.submitButton.setAttribute('aria-disabled', 'true');
this.submitButton.dataset.underBrimBlocked = 'true';
this.submitButton.classList.add('under-brim-message--blocked');
} else if (this.submitButton.dataset.underBrimBlocked === 'true') {
this.submitButton.removeAttribute('aria-disabled');
delete this.submitButton.dataset.underBrimBlocked;
this.submitButton.classList.remove('under-brim-message--blocked');
}
}

onFormSubmit() {
if (!this.checkbox) return;
// Read the current, about-to-be-submitted values. Nothing mutates these
// fields synchronously during submit, so this stays consistent with
// whatever Dawn's own product-form.js reads a moment later.
const quantityInput = this.form.querySelector('input[name="quantity"]');
this.pendingSubmission = this.checkbox.checked
? {
addonId: this.addonIdInput ? this.addonIdInput.value : null,
quantity: quantityInput ? parseInt(quantityInput.value, 10) || 1 : 1,
}
: null;
}

async handleCartUpdate() {
const pending = this.pendingSubmission;
this.pendingSubmission = null;
if (!pending || !pending.addonId) return;

const addonVariantId = this.dataset.addonVariantId;
if (!addonVariantId) return;

try {
const sectionIds = collectSectionIds();
const response = await fetch(
'/cart/add.js',
fetchConfigJson({
id: addonVariantId,
quantity: pending.quantity,
properties: { _addon_id: pending.addonId },
sections: Object.values(sectionIds),
sections_url: window.location.pathname,
})
).then((r) => r.json());

if (response && response.sections) {
applyCartResponse(sectionIds, response.sections);
} else {
await refreshCartUI();
}
} catch (error) {
console.error('[under-brim-message] failed to add customization', error);
} finally {
// Reset the control back to its default (off) state so a second,
// separate "Add to cart" click doesn't quietly reuse the same
// message/linking id for a different line.
if (this.checkbox) this.checkbox.checked = false;
if (this.textarea) {
this.textarea.value = '';
this.textarea.disabled = true;
}
if (this.addonIdInput) {
this.addonIdInput.value = '';
this.addonIdInput.disabled = true;
}
this.updateCounter();
this.updateSubmitState();
}
}
}

if (!customElements.get('under-brim-message')) {
customElements.define('under-brim-message', UnderBrimMessage);
}

/* -------------------------------------------------------------------- *
* Sitewide cart reconciliation                                         *
* -------------------------------------------------------------------- */

window.UnderBrimMessage.subscribe(window.UnderBrimMessage.PUB_SUB_EVENTS.cartUpdate, (event) => {
// 'product-form' cart-update events (a hat being added) are already handled
// directly and synchronously by that <under-brim-message> instance's own
// handleCartUpdate(), which adds the linked addon line itself. Also scheduling
// a reconcile pass here would race that in-flight /cart/add.js call — the
// reconcile pass could read /cart.js before the addon line exists yet and
// add a second, duplicate addon line for the same hat. Skip it for that
// source; if handleCartUpdate ever fails outright, the unconditional
// scheduleReconcile(600) below (and the one on every future page load) still
// heals the cart.
if (event && (event.source === 'product-form' || event.source === 'under-brim-message-reconcile')) return;
scheduleReconcile();
});
// Also catch up once on page load, in case the cart already drifted (e.g. a
// previous session was interrupted between adding the hat and the add-on).
scheduleReconcile(600);
})();